import React from "react";
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { Link } from "react-router-dom";

import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";

import logo from "../../images/Ethos Hub Logo Long.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { css } from "styled-components/macro"; //eslint-disable-line

const Header = tw.header`
  w-full max-w-screen-xl mx-auto
  px-4 lg:px-8 py-3
  flex items-center justify-between
`;

export const NavLinks = tw.div`flex items-center`;
export const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:my-0 lg:mx-4
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-primary-500 hocus:text-primary-500
`;

export const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-6 py-3 rounded-3xl bg-primary-500 text-gray-100
  hocus:bg-primary-700 hocus:text-gray-200 focus:shadow-md
  border-b-0
`;

export const LogoLink = styled(Link)`
  ${tw`flex items-center border-b-0`};
  img { ${tw`h-24 w-auto m-0`} } /* big logo */
`;

export const MobileNavLinksContainer = tw.nav`flex w-full items-center justify-between lg:hidden`;
export const NavToggle = tw.button`lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300`;

export const MobileNavLinks = motion(styled.div`
  ${tw`fixed top-0 inset-x-0 z-10 mx-4 my-6 p-8 border text-center rounded-lg bg-white text-gray-900`}
  ${NavLinks} { ${tw`flex flex-col items-center`} }
`);

export const DesktopBar = tw.nav`hidden lg:flex w-full items-center`;
export const DesktopNavLinks = DesktopBar;
const DesktopLeft  = tw.div`flex-shrink-0`;
const DesktopCenter = tw.nav`flex-1 flex justify-center`;
const DesktopRight = tw.div`flex-shrink-0 flex items-center gap-6`;
const DemoButton = tw.a`px-6 py-3 rounded-full bg-primary-500 text-white font-bold text-sm hocus:bg-primary-700 transition duration-300`;
const collapseBreakPointCssMap = {
  sm: { mobileNavLinks: tw`sm:hidden`, desktopNavLinks: tw`sm:flex`, mobileNavLinksContainer: tw`sm:hidden` },
  md: { mobileNavLinks: tw`md:hidden`, desktopNavLinks: tw`md:flex`, mobileNavLinksContainer: tw`md:hidden` },
  lg: { mobileNavLinks: tw`lg:hidden`, desktopNavLinks: tw`lg:flex`, mobileNavLinksContainer: tw`lg:hidden` },
  xl: { mobileNavLinks: tw`xl:hidden`, desktopNavLinks: tw`xl:flex`, mobileNavLinksContainer: tw`xl:hidden` },
};

const APP_URL = process.env.REACT_APP_APP_URL || "https://app.ethoshub.io";

export default function LightHeader({
  roundedHeaderButton = false,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "lg",
}) {
  // Center menu links
  const centerLinks = (
    <NavLinks>
      <Link to="/"><NavLink as="span">Home</NavLink></Link>
      <Link to="/AboutUs"><NavLink as="span">About</NavLink></Link>
      <Link to="/blog"><NavLink as="span">Blog</NavLink></Link>
      <Link to="/PricingPage"><NavLink as="span">Pricing</NavLink></Link>
      <Link to="/contact"><NavLink as="span">Contact Us</NavLink></Link>
    </NavLinks>
  );

  // For mobile we show everything in one stack
  const mobileLinks = (
    <NavLinks>
      {centerLinks}
      <div tw="mt-4 flex items-center space-x-4 justify-center">
        <a href={`${APP_URL}/login`}><NavLink as="span">Log in</NavLink></a>
        <DemoButton href="/contact">Book a demo</DemoButton>
      </div>
    </NavLinks>
  );

  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  const defaultLogoLink = (
    <LogoLink to="/" aria-label="Ethos Hub home">
      <img src={logo} alt="Ethos Hub" />
    </LogoLink>
  );

  const finalLogo = logoLink || defaultLogoLink;

  return (
    <Header className={className || "header-light"}>
      {/* Desktop (3 zones) */}
      <DesktopBar css={collapseBreakpointCss.desktopNavLinks}>
        <DesktopLeft>{finalLogo}</DesktopLeft>
        <DesktopCenter>{centerLinks}</DesktopCenter>
        <DesktopRight>
          <a href={`${APP_URL}/login`}><NavLink as="span">Log in</NavLink></a>
          <DemoButton href="/contact">Book a demo</DemoButton>
        </DesktopRight>
      </DesktopBar>

      {/* Mobile */}
      <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
        {finalLogo}
        <MobileNavLinks
          initial={{ x: "150%", display: "none" }}
          animate={animation}
          css={collapseBreakpointCss.mobileNavLinks}
        >
          {mobileLinks}
        </MobileNavLinks>
        <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"}>
          {showNavLinks ? <CloseIcon tw="w-6 h-6" /> : <MenuIcon tw="w-6 h-6" />}
        </NavToggle>
      </MobileNavLinksContainer>
    </Header>
  );
}